// Direct & indirect objects — telling them apart, the two pronoun sets
// (lo/la/los/las vs le/les), how they combine (le/les → se), and the
// redundant-pronoun habit. A side-by-side pronoun table closes the page.
import { renderGuideSections } from "../ui/guideSections.js";

export function objectsView(app) {
  const table = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">Object Pronouns at a Glance</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Person</th>
          <th class="py-2 px-3">Direct (what)</th>
          <th class="py-2 px-3">Indirect (to/for whom)</th>
        </tr></thead>
        <tbody>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">me</td><td class="py-2 px-3">me</td><td class="py-2 px-3">me</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">you (inf.)</td><td class="py-2 px-3">te</td><td class="py-2 px-3">te</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">him / it / you (f.)</td><td class="py-2 px-3">lo</td><td class="py-2 px-3">le</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">her / it / you (f.)</td><td class="py-2 px-3">la</td><td class="py-2 px-3">le</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">us</td><td class="py-2 px-3">nos</td><td class="py-2 px-3">nos</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">you all (Spain)</td><td class="py-2 px-3">os</td><td class="py-2 px-3">os</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">them (m.) / you all</td><td class="py-2 px-3">los</td><td class="py-2 px-3">les</td></tr>
          <tr><td class="py-2 pr-3 font-medium">them (f.) / you all</td><td class="py-2 px-3">las</td><td class="py-2 px-3">les</td></tr>
        </tbody>
      </table></div>
      <p class="text-sage-500 text-xs mt-3">Only the third person differs (lo/la/los/las vs le/les). When both appear, <b>le/les → se</b>: <i>Se lo doy.</i></p>
    </div>`;

  renderGuideSections(
    app,
    "Direct & Indirect Objects",
    "Most verbs act on something. The direct object is what the action lands on; the indirect object is who benefits from it. Spanish marks the two with different pronouns — and the difference only really shows up in the third person.",
    [
      {
        title: "1. Spotting which is which",
        body: `
          <p class="text-sage-700 mb-2">Ask two questions about the verb:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5 mb-2">
            <li><b>What / whom?</b> → direct object. <i>Compro <u>el pan</u>.</i> (I buy the bread.)</li>
            <li><b>To whom / for whom?</b> → indirect object. <i>Compro el pan <u>para mi madre</u>.</i></li>
          </ul>
          <p class="text-sage-700 text-sm">A sentence can have both: <i>Le compro el pan</i> — <i>el pan</i> (direct) and <i>le</i> = to her (indirect).</p>
        `,
      },
      {
        title: "2. Replacing the noun with a pronoun",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>Direct:</b> <i>Veo <u>la película</u> → <b>La</b> veo.</i></li>
            <li><b>Indirect:</b> <i>Hablo <u>a Juan</u> → <b>Le</b> hablo.</i></li>
            <li>Placement is the same as other object pronouns: before a conjugated verb (<i>Lo veo</i>), or attached to an infinitive/gerund/affirmative command (<i>verlo, viéndolo, ¡dímelo!</i>).</li>
          </ul>
        `,
      },
      {
        title: "3. Combining both — and le/les → se",
        body: `
          <p class="text-sage-700 mb-2">When you use both pronouns, the order is <b>indirect before direct</b>. And to avoid two l-words in a row, <b>le/les becomes se</b>:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>Doy el libro a María → Le doy el libro → <b>Se</b> lo doy.</i></li>
            <li><i>Mandé las fotos a ellos → Les mandé las fotos → <b>Se</b> las mandé.</i></li>
          </ul>
        `,
      },
      {
        title: "4. The redundant pronoun (it's normal!)",
        body: `
          <p class="text-sage-700 mb-2">Spanish often uses the indirect pronoun <b>even when the noun is right there</b> — it's not a mistake, it's the norm:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i><b>Le</b> di el regalo <b>a Ana</b>.</i> (Both <i>le</i> and <i>a Ana</i>.)</li>
            <li>For emphasis or clarity, add <i>a + prepositional pronoun</i>: <i><b>Le</b> hablo <b>a él</b></i> (since <i>le</i> alone is ambiguous).</li>
          </ul>
          <p class="text-sage-700 text-sm mt-3"><b>Heads-up — leísmo:</b> in parts of Spain you'll hear <i>le</i> as a direct object for a male person (<i>Le vi</i> instead of <i>Lo vi</i>). It's widely accepted for people, but the textbook-standard direct object stays <i>lo/la</i>.</p>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
